import React, { useState, useEffect } from "react";
import { ShoppingCart, FileText, Package, Search, RotateCcw, X, CircleChevronLeft } from "lucide-react";
import totalOrderIcon from "../../assets/images/totalOrderIcon.png";
import OrderDeliveryIcon from "../../assets/images/OrderDeliverIcon.png";
import labTestIcon from "../../assets/images/LabTestIcon.png";
import RevenueIcon from "../../assets/images/RevenueIcon.png";
import medicineIcon from "../../assets/images/medicineIcon.png";
import defaultAvatar from "../../assets/images/default_avatar.jpg";
import { useAuth } from "../../context/AuthContext";
import { OrderService } from "../../services/api/order.service";
import { AdminOrderService } from "../../services/api/AdminOrderService/admin.order.service";
import { Product } from "../Inventory-Stocks & Orders/Admin/StockOrder/PrepareOrder/types";
import { ReturnItemService } from "../../services/api/ReturnItem/returnItem.service";
import toast from "react-hot-toast";
import { DataTable } from "../DataTable/DataTable";
import { InverntroyService } from "../../services/api/inventroy.service";

interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  changePercentage: number;
  changeText: string;
  onClick?: () => void;
}

interface OrderItem {
  id: string;
  productName: string;
  quantity: string;
  price: string;
  date: string;
}

interface DashboardData {
  totalOrders: {
    count: number;
    change: number;
    trend: string;
  };
  totalOrdersDelivered: {
    count: number;
    change: number;
    trend: string;
  };
  labTestsConducted: {
    count: number;
    change: number;
    trend: string;
  };
  revenue: {
    amount: number;
    change: number;
    trend: string;
  };
}

const StatCard: React.FC<StatCardProps> = ({ title, value, icon, changePercentage, changeText, onClick }) => {
  const isPositive = changePercentage >= 0;

  return (
    <div className="bg-white rounded-lg p-4 shadow-sm" onClick={onClick}>
      <div className="flex justify-between items-start">
        <div>
          <p className="text-gray-500 text-sm">{title}</p>
          <h3 className="text-2xl font-semibold mt-1">{value}</h3>
        </div>
        <div className="w-10 h-10 rounded-full bg-green-50 flex items-center justify-center">{icon}</div>
      </div>
      <div className="mt-3">
        <span className={`text-sm ${isPositive ? "text-green-500" : "text-red-500"} flex items-center`}>
          {isPositive ? "↑" : "↓"} {Math.abs(changePercentage)}% {changeText}
        </span>
      </div>
    </div>
  );
};

// OrderDetails Component
const OrderDetails = ({ activeTab, orders, onTabChange }) => {
  const [loading, setLoading] = useState(false);
  const [medicineOrders, setMedicineOrders] = useState([]);
  const [labOrders, setLabOrders] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const { user } = useAuth();
  const clinicId = user?.role === "clinic" ? user?.id : "";

  useEffect(() => {
    let isMounted = true;

    const fetchOrders = async () => {
      try {
        setLoading(true);
        const response = await AdminOrderService.getAdminOrdersListing(currentPage, pageSize, {
          isLabtestOrder: activeTab === "lab-test-order",
          clinicId,
        });

        console.log("Raw API Response:", response);

        if (!isMounted) return;

        const orders = response?.data?.data || [];
        const totalItemsCount = response?.data?.pagination?.totalCount || 0;
        const totalPagesCount = response?.data?.pagination?.totalPages || 1;
        const totalPageSize = response?.data?.pagination?.pageSize;

        setTotalCount(totalItemsCount);
        setTotalPages(totalPagesCount);
        setPageSize(totalPageSize);

        const transformedOrders = orders.map((order: any) => {
          const labStatus = order.orderStatus === "PENDING_ADMIN" ? "new" : "";

          const patientName = order.patientName || order.patient?.name || `Patient ${order.patientId?.slice(0, 6) || "XXXXXX"}`;

          return {
            ...order,
            id: order.id,
            patientName,
            orderNumber: order.orderNumber || `ORD-${order.id}`,
            orderType: order.orderType || "UNKNOWN",
            orderDate: new Date(order.orderDate).toLocaleDateString(),
            details: (order.productOrderItems || []).map((item: any) => item.productName).join(", "),
            labStatus,
            hasPrescription: order.hasPrescription === true,
            paymentStatus: order.paymentStatus || "Pending",
            status: (order.paymentStatus || "Pending").toLowerCase(),
          };
        });

        const medOrders = transformedOrders.filter((order) => order.orderType?.toUpperCase().includes("DIRECT"));

        const labOrders = transformedOrders.filter((order) => order.orderType?.toUpperCase().includes("LAB_TEST"));

        console.log("med", medOrders, "lab", labOrders);

        if (isMounted) {
          setMedicineOrders(medOrders);
          setLabOrders(labOrders);
        }
      } catch (err) {
        console.error("Fetch Error:", err);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchOrders();

    return () => {
      isMounted = false;
    };
  }, [currentPage, activeTab]);

  // Determine which orders to display based on activeTab
  const displayedOrders = activeTab === "medical-orders" ? medicineOrders : labOrders;
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 h-[265px]">
      <div className="flex border-b border-gray-200 mb-6">
        <div className="flex items-center space-x-4">
          <div
            onClick={() => onTabChange("medical-orders")}
            className={`flex items-center space-x-2 px-4 py-2 cursor-pointer border-b-2 ${activeTab === "medical-orders" ? "border-green-500 text-green-600" : "border-transparent text-gray-500"
              }`}
          >
            <ShoppingCart size={16} />
            <span className="font-medium">Medical Orders</span>
          </div>
          <div
            onClick={() => onTabChange("lab-test-order")}
            className={`flex items-center space-x-2 px-4 py-2 cursor-pointer border-b-2 ${activeTab === "lab-test-order" ? "border-green-500 text-green-600" : "border-transparent text-gray-500"
              }`}
          >
            <FileText size={16} />
            <span className="font-medium">Lab test orders</span>
          </div>
        </div>
      </div>
      <div
        className="h-[150px] overflow-y-auto"
        style={{
          scrollbarWidth: "thin",
          scrollBehavior: "smooth",
        }}
      >
        <div className="space-y-4">
          {displayedOrders.map((order, index) => (
            <div key={index} className="flex items-center space-x-4 p-4 border border-gray-100 rounded-lg hover:bg-gray-50">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                {order.orderType?.toUpperCase().includes("PRESCRIPTION") ? (
                  <ShoppingCart className="text-green-600" size={20} />
                ) : (
                  <Package className="text-green-600" size={20} />
                )}
              </div>
              <div className="flex-1">
                <h3 className="font-medium text-gray-900">{order.orderNumber}</h3>
                <p className="text-sm text-gray-500">Order ID: {order.id}</p>
              </div>
              <div className="text-right text-sm text-gray-500">{order.orderDate}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// InventoryDetails Component
const InventoryDetails = ({ activeInventoryTab, inventoryData, onTabChange }) => {
  const [availableProducts, setAvailableProducts] = useState<Product[]>([]);
  const [invoiceHistory, setInvoiceHistory] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();
  const clinicId = user?.role === "clinic" ? user?.id : "";

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await AdminOrderService.getAdminProductListing();

        // Transform the API data to match the expected Product type
        const transformedProducts = response.map((item: any) => ({
          product_id: item.product_id || item.id,
          product_name: item.product?.name || "Unknown Product",
          manufacturer_name: item.manufacturer?.manufacturer_name || "Unknown Manufacturer",
          product_image: item.product?.productImage || medicineIcon,
          landing_cost: parseFloat(item.landing_cost) || 0,
          mrp: parseFloat(item.mrp) || 0,
          quantity: item.no_of_items || 0,
          expiry_date: item.expiry_date,
          batch_number: item.batch_number,
          hsn_number: item.hsn_number,
          product_rate: parseFloat(item.product_rate) || 0,
          discount: parseFloat(item.discount) || 0,
          cgst: parseFloat(item.cgst) || 0,
          sgst: parseFloat(item.sgst) || 0,
          free_items: item.free_items || 0,
        }));

        console.log("Transformed Products:", transformedProducts);

        setAvailableProducts(transformedProducts);
      } catch (err) {
        console.error("Failed to fetch products:", err);
        setError(err instanceof Error ? err.message : "Failed to load products");
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  const formatDate = (dateString: string | undefined): string => {
    if (!dateString) return "-";

    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "-";

      return date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch (error) {
      console.error("Error formatting date:", error);
      return "-";
    }
  };
  const formatExpiryDate = (dateString: string | undefined): string => {
    if (!dateString) return "-";

    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "-";

      return date.toLocaleDateString("en-IN", {
        month: "short", // e.g., Jan, Feb, Mar
        year: "numeric", // e.g., 2025
      });
    } catch (error) {
      console.error("Error formatting date:", error);
      return "-";
    }
  };


  useEffect(() => {
    const fetchInvoiceHistory = async () => {
      try {
        const response = await InverntroyService.getInvoiceHistoryForClinic(1, 10, clinicId);
        console.log("inventory", response);
        setInvoiceHistory(response.data);
      } catch (error) {
        console.error("Failed to fetch invoice history:", error);
      }
    };
    if (activeInventoryTab === "invoice-history") {
      fetchInvoiceHistory();
    }
  }, [activeInventoryTab, inventoryData?.clinicId]);

  return (
    <div
      className="bg-white rounded-lg shadow-sm border border-gray-200 h-[265px] -mt-5"
      style={{
        scrollBehavior: "smooth",
        scrollbarWidth: "thin",
      }}
    >
      <div className="flex border-b border-gray-200 mb-6">
        <div className="flex items-center space-x-4">
          <div
            onClick={() => onTabChange("inventory-list")}
            className={`px-4 py-2 cursor-pointer border-b-2 ${activeInventoryTab === "inventory-list" ? "border-green-500 text-green-600" : "border-transparent text-gray-500"
              }`}
          >
            <span className="font-medium">Inventory list</span>
          </div>
          <div
            onClick={() => onTabChange("invoice-history")}
            className={`px-4 py-2 cursor-pointer border-b-2 ${activeInventoryTab === "invoice-history" ? "border-green-500 text-green-600" : "border-transparent text-gray-500"
              }`}
          >
            <span className="font-medium">Invoice History</span>
          </div>
        </div>
      </div>
      <div
        className="h-[150px] overflow-y-auto"
        style={{
          scrollbarWidth: "thin",
          scrollBehavior: "smooth",
        }}
      >
        <div className="overflow-x-auto px-4">
          {activeInventoryTab === "inventory-list" ? (
            // Inventory List Tab Content
            <table className="w-full ">
              <thead className="border-b border-gray-200">
                <tr className="text-left">
                  <th className="pb-3 text-sm font-medium">S.No.</th>
                  <th className="pb-3 text-sm font-medium">Product Name</th>
                  <th className="pb-3 text-sm font-medium">Quantity</th>
                  <th className="pb-3 text-sm font-medium">Rate</th>
                  <th className="pb-3 text-sm font-medium">MRP</th>
                  <th className="pb-3 text-sm font-medium">Expiry Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {availableProducts.map((product, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="py-3 text-sm text-gray-500 font-medium">{index + 1}</td>
                    <td className="py-3 text-sm text-gray-900 flex items-center">
                      <img src={product.product_image} alt={product.product_name} className="w-8 h-8 rounded-full mr-2" />
                      {product.product_name}
                    </td>
                    <td className="py-3 text-sm text-gray-900">{product.quantity}</td>
                    <td className="py-3 text-sm text-gray-900">₹{product.product_rate}</td>
                    <td className="py-3 text-sm text-gray-900">₹{product.mrp}</td>
                    <td className="py-3 text-sm text-gray-900">{formatExpiryDate(product.expiry_date)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            // Invoice History Tab Content
            <div className="w-full">
              <table className="w-full">
                <thead className="border-b border-gray-200 sticky top-0 bg-white">
                  <tr className="text-left">
                    <th className="pb-3 text-sm text-gray-500 font-medium">S.No.</th>
                    <th className="pb-3 text-sm text-gray-500 font-medium">Invoice Number</th>
                    <th className="pb-3 text-sm text-gray-500 font-medium">Invoice date</th>
                    <th className="pb-3 text-sm text-gray-500 font-medium">Invoice Amount</th>
                    <th className="pb-3 text-sm text-gray-500 font-medium">No of Products</th>
                  </tr>
                </thead>
              </table>
              <div className="h-[150px] overflow-y-auto" style={{ scrollbarWidth: "thin" }}>
                <table className="w-full">
                  <tbody className="divide-y divide-gray-100">
                    {invoiceHistory.map((item, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="py-3 text-sm text-gray-500 font-medium w-[60px]">{index + 1}</td>
                        <td className="py-3 text-sm text-gray-900 w-1/4">{item.invoice_number}</td>
                        <td className="py-3 text-sm text-gray-900 w-1/4">{formatDate(item.invoice_date)}</td>
                        <td className="py-3 text-sm text-gray-900 w-1/4">₹ {item.invoice_amount}</td>
                        <td className="py-3 text-sm text-gray-900 w-1/4 text-center">{item.number_of_products}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

interface ReturnManagementProps {
  onShowReturnHistory: () => void;
}

const ReturnManagement: React.FC<ReturnManagementProps> = ({ onShowReturnHistory }) => {
  const [activeTab, setActiveTab] = useState("request");
  const [returnRequestData, setReturnRequestData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [returnAcceptedDataCount, setReturnAcceptedDataCount] = useState(0);
  const [returnItemDetails, setReturnItemDetails] = useState<any>(null);
  const [showModal, setShowModal] = useState(false);
  const [returnAcceptedData, setReturnAcceptedData] = useState<any>([]);
  const [modalType, setModalType] = useState<"request" | "accepted">("request");
  const { user } = useAuth();
  const clinicId = user?.role === "clinic" ? user?.id : "";
  const [searchText, setSearchText] = useState("");
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);

  const [requestPagination, setRequestPagination] = useState({
    page: 1,
    pageSize: 10,
    totalPages: 1,
    hasNextPage: false,
    hasPrevPage: false,
  });

  const [acceptedPagination, setAcceptedPagination] = useState({
    page: 1,
    pageSize: 10,
    totalPages: 1,
    hasNextPage: false,
    hasPrevPage: false,
  });

  useEffect(() => {
    const getReturnRequest = async () => {
      try {
        const response = await ReturnItemService.getReturnItemListing({
          page: requestPagination.page,
          pageSize: requestPagination.pageSize,
          searchText: searchText,
          clinicId: clinicId,
        });
        setReturnRequestData(response.returnOrders.data || []);
        setTotalCount(response.returnOrders.pagination?.totalCount || 0);

        // Update pagination info
        setRequestPagination((prev) => ({
          ...prev,
          totalPages: response.returnOrders?.pagination?.totalPages || 1,
          hasNextPage: response.returnOrders?.pagination?.hasNextPage || false,
          hasPrevPage: response.returnOrders?.pagination?.hasPrevPage || false,
        }));
      } catch (error) {
        console.error(error);
        setReturnRequestData([]);
        setTotalCount(0);
      }
    };

    getReturnRequest();
  }, [requestPagination.page, requestPagination.pageSize, searchText, activeTab]);

  // Update the useEffect for accepted returns
  useEffect(() => {
    const getReturnAcceptedRequest = async () => {
      try {
        const response = await ReturnItemService.getReturnAcceptedItemListing({
          page: acceptedPagination.page,
          pageSize: acceptedPagination.pageSize,
          searchText: searchText,
          clinicId: clinicId,
        });
        setReturnAcceptedData(response.returnOrders.data || []);
        setReturnAcceptedDataCount(response.returnOrders.pagination?.totalCount || 0);

        // Update pagination info
        setAcceptedPagination((prev) => ({
          ...prev,
          totalPages: response.returnOrders.pagination?.totalPages || 1,
          hasNextPage: response.returnOrders.pagination?.hasNextPage || false,
          hasPrevPage: response.returnOrders.pagination?.hasPrevPage || false,
        }));
      } catch (error) {
        console.error(error);
        setReturnAcceptedData([]);
        setReturnAcceptedDataCount(0);
      }
    };

    getReturnAcceptedRequest();
  }, [acceptedPagination.page, acceptedPagination.pageSize, searchText, activeTab]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchText(value);

    // Clear previous timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    // Set new timeout with debounce (500ms)
    setSearchTimeout(
      setTimeout(() => {
        // Reset to first page when searching for both paginations
        setRequestPagination((prev) => ({ ...prev, page: 1 }));
        setAcceptedPagination((prev) => ({ ...prev, page: 1 }));
      }, 500)
    );
  };

  // Clear timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTimeout]);

  // Add pagination handlers
  const handleRequestPageChange = (newPage: number) => {
    setRequestPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handleAcceptedPageChange = (newPage: number) => {
    setAcceptedPagination((prev) => ({ ...prev, page: newPage }));
  };

  const refreshReturnRequests = async () => {
    try {
      if (activeTab === "request") {
        const requestResponse = await ReturnItemService.getReturnItemListing({
          page: requestPagination.page,
          pageSize: requestPagination.pageSize,
          searchText: searchText,
          clinicId: clinicId,
        });
        setReturnRequestData(requestResponse.returnOrders.data || []);
        setTotalCount(requestResponse.returnOrders.pagination?.totalCount || 0);
        setRequestPagination((prev) => ({
          ...prev,
          totalPages: requestResponse.returnOrders.pagination?.totalPages || 1,
          hasNextPage: requestResponse.returnOrders.pagination?.hasNextPage || false,
          hasPrevPage: requestResponse.returnOrders.pagination?.hasPrevPage || false,
        }));
      } else {
        const acceptedResponse = await ReturnItemService.getReturnAcceptedItemListing({
          page: acceptedPagination.page,
          pageSize: acceptedPagination.pageSize,
          searchText: searchText,
          clinicId: clinicId,
        });
        setReturnAcceptedData(acceptedResponse.returnOrders?.data || []);
        setReturnAcceptedDataCount(acceptedResponse.returnOrders?.pagination?.totalCount || 0);
        setAcceptedPagination((prev) => ({
          ...prev,
          totalPages: acceptedResponse.returnOrders?.pagination?.totalPages || 1,
          hasNextPage: acceptedResponse.returnOrders?.pagination?.hasNextPage || false,
          hasPrevPage: acceptedResponse.returnOrders?.pagination?.hasPrevPage || false,
        }));
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to refresh data");
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setReturnItemDetails(null);
  };

  const handleReturnDetails = async (order: any, type: "request" | "accepted") => {
    try {
      setReturnItemDetails(order);
      setModalType(type);
      setShowModal(true);
    } catch (error) {
      console.log(error);
    }
  };

  const handleAcceptReturn = async (orderId: number) => {
    try {
      const response = await ReturnItemService.updateReturnAcceptItemStatus(orderId, "returned_accepted");
      if (response.success) {
        toast.success("Return accepted successfully");
        refreshReturnRequests();
        setShowModal(false);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleRejectReturn = async (orderId: number) => {
    try {
      const response = await ReturnItemService.updateReturnAcceptItemStatus(orderId, "returned_rejected");
      if (response.success) {
        toast.success("Return rejected");
        refreshReturnRequests();
        setShowModal(false);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleUpdateInventory = async (orderId: number) => {
    try {
      await ReturnItemService.updateReturnCompletedItemStatus(orderId);

      toast.success("Inventory updated successfully");
      refreshReturnRequests();
      setShowModal(false);
    } catch (error) {
      console.log(error);
      toast.error("Failed to update inventory");
    }
  };

  return (
    <div className="w-full mx-auto bg-white">
      {/* Header with Tabs */}
      <div className="flex items-center justify-between border-b border-gray-200">
        <div className="flex">
          {/* Return Request Tab */}
          <button
            onClick={() => setActiveTab("request")}
            className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${activeTab === "request"
              ? "text-gray-900 border-gray-900"
              : "text-gray-500 border-transparent hover:text-gray-700 hover:border-gray-300"
              }`}
          >
            Return Request ({totalCount})
          </button>

          {/* Return Accepted Tab */}
          <button
            onClick={() => setActiveTab("accepted")}
            className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${activeTab === "accepted"
              ? "text-teal-600 border-teal-600"
              : "text-gray-500 border-transparent hover:text-gray-700 hover:border-gray-300"
              }`}
          >
            Return Accepted ({returnAcceptedDataCount})
          </button>
        </div>

        {/* Right side controls */}
        <div className="flex items-center gap-4 px-6">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search..."
              value={searchText}
              onChange={handleSearch}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            {searchText && (
              <button
                onClick={() => {
                  setSearchText("");
                  setRequestPagination((prev) => ({ ...prev, page: 1 }));
                  setAcceptedPagination((prev) => ({ ...prev, page: 1 }));
                }}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Return History */}
          <button className="flex items-center gap-2 text-blue-500 text-sm hover:text-blue-600" onClick={onShowReturnHistory}>
            <RotateCcw className="w-4 h-4" />
            Return History
          </button>
        </div>
      </div>

      {/* Table Content */}
      {activeTab === "request" ? (
        <>
          {/* Request Table Header */}
          <div className="grid grid-cols-12 gap-4 px-6 py-4 bg-gray-50 text-sm font-medium text-gray-700 border-b border-gray-200">
            <div className="col-span-2">Order & Order ID</div>
            <div className="col-span-3">Patient Name & Number</div>
            <div className="col-span-2">No.of Item Return</div>
            <div className="col-span-2">Total Price</div>
            <div className="col-span-2">Reason For Return</div>
            <div className="col-span-1">Action</div>
          </div>
          {/* Request Table Body */}
          <div className="divide-y divide-gray-100">
            {returnRequestData.length > 0 ? (
              returnRequestData.map((item, index) => (
                <div key={`request-${index}`} className="grid grid-cols-12 gap-4 px-6 py-4 hover:bg-gray-50">
                  {/* Order & Order ID */}
                  <div className="col-span-2 flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center">
                      <img
                        src={item.ProductDetails?.productImage || defaultAvatar}
                        alt="product-image"
                        className="w-full h-full object-cover"
                      />
                    </div>

                    <div>
                      <div className="text-sm font-medium text-gray-900">{item.orderNumber}</div>
                      <div className="text-xs text-gray-500">Order ID: {item.id}</div>
                    </div>
                  </div>

                  {/* Patient Name & Number */}
                  <div className="col-span-3 flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center">
                      <img
                        src={item.patientDetails?.patientProfileImage || defaultAvatar}
                        alt="patient"
                        className="w-full h-full rounded-full object-cover"
                      />
                    </div>

                    <div>
                      <div className="text-sm font-medium text-gray-900">{item.patientDetails?.patientName}</div>
                      <div className="text-xs text-gray-500">{item.patientDetails?.patientMobileNumber}</div>
                    </div>
                  </div>

                  {/* No.of Item Return */}
                  <div className="col-span-2 flex items-center">
                    <span className="text-sm font-medium text-gray-900">{item.returnItemsCount}</span>
                  </div>

                  {/* Total Price */}
                  <div className="col-span-2 flex items-center">
                    <span className="text-sm font-medium text-gray-900">₹{item.returnTotal}</span>
                  </div>

                  {/* Reason For Return */}
                  <div className="col-span-2 flex items-center">
                    <span className="text-xs text-gray-500">{item.returnReason || "N/A"}</span>
                  </div>

                  {/* Action */}
                  <div className="col-span-1 flex items-center justify-center gap-2">
                    {item.returnStatus === "RETURN_APPROVED" ? (
                      <span className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 border border-gray-200 rounded-md">
                        Approved
                      </span>
                    ) : item.returnStatus === "RETURN_REJECTED" ? (
                      <span className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 border border-gray-200 rounded-md">
                        Rejected
                      </span>
                    ) : (
                      <button onClick={() => handleReturnDetails(item, "request")}>
                        <span className="px-3 py-1.5 text-xs font-medium text-teal-600 bg-teal-50 border border-teal-200 rounded-md hover:bg-teal-100 transition-colors">
                          View
                        </span>
                      </button>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <>
                <div className="col-span-12 py-8 text-center text-gray-500">
                  {searchText ? `No results found for "${searchText}"` : "No data found"}
                </div>
              </>
            )}
          </div>
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200">
            <div className="text-sm text-gray-500">
              Showing page {activeTab === "request" ? requestPagination.page : acceptedPagination.page} of{" "}
              {activeTab === "request" ? requestPagination.totalPages : acceptedPagination.totalPages}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">Rows per page:</span>
              <select
                value={activeTab === "request" ? requestPagination.pageSize : acceptedPagination.pageSize}
                onChange={(e) => {
                  const newSize = Number(e.target.value);
                  if (activeTab === "request") {
                    setRequestPagination((prev) => ({
                      ...prev,
                      pageSize: newSize,
                      page: 1,
                    }));
                  } else {
                    setAcceptedPagination((prev) => ({
                      ...prev,
                      pageSize: newSize,
                      page: 1,
                    }));
                  }
                }}
                className="text-sm border rounded-md px-2 py-1"
              >
                <option value="5">5</option>
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="50">50</option>
              </select>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() =>
                  activeTab === "request"
                    ? handleRequestPageChange(requestPagination.page - 1)
                    : handleAcceptedPageChange(acceptedPagination.page - 1)
                }
                disabled={activeTab === "request" ? !requestPagination.hasPrevPage : !acceptedPagination.hasPrevPage}
                className={`px-3 py-1 text-sm rounded-md ${(activeTab === "request" ? !requestPagination.hasPrevPage : !acceptedPagination.hasPrevPage)
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                  }`}
              >
                Previous
              </button>
              <button
                onClick={() =>
                  activeTab === "request"
                    ? handleRequestPageChange(requestPagination.page + 1)
                    : handleAcceptedPageChange(acceptedPagination.page + 1)
                }
                disabled={activeTab === "request" ? !requestPagination.hasNextPage : !acceptedPagination.hasNextPage}
                className={`px-3 py-1 text-sm rounded-md ${(activeTab === "request" ? !requestPagination.hasNextPage : !acceptedPagination.hasNextPage)
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                  }`}
              >
                Next
              </button>
            </div>
          </div>
        </>
      ) : (
        <>
          {/* Accepted Table Header */}
          <div className="grid grid-cols-12 gap-4 px-6 py-4 bg-gray-50 text-sm font-medium text-gray-700 border-b border-gray-200">
            <div className="col-span-2">Order & Order ID</div>
            <div className="col-span-3">Patient Name & Number</div>
            <div className="col-span-2">No.of Item Return</div>
            <div className="col-span-2">Total Price</div>
            <div className="col-span-2">Return Status</div>
            <div className="col-span-1">Actions</div>
          </div>
          {/* Accepted Table Body */}
          <div className="divide-y divide-gray-100">
            {returnAcceptedData.length > 0 ? (
              returnAcceptedData.map((item, index) => (
                <div key={`accepted-${index}`} className="grid grid-cols-12 gap-4 px-6 py-4 hover:bg-gray-50">
                  {/* Order & Order ID */}
                  <div className="col-span-2 flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center">
                      <img
                        src={item.ProductDetails?.productImage || defaultAvatar}
                        alt="product-image"
                        className="w-full h-full object-cover"
                      />
                    </div>

                    <div>
                      <div className="text-sm font-medium text-gray-900">{item.orderNumber}</div>
                      <div className="text-xs text-gray-500">Order ID: {item.id}</div>
                    </div>
                  </div>

                  {/* Patient Name & Number */}
                  <div className="col-span-3 flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center">
                      <img
                        src={item.patientDetails?.patientProfileImage || defaultAvatar}
                        alt="patient"
                        className="w-full h-full rounded-full object-cover"
                      />
                    </div>

                    <div>
                      <div className="text-sm font-medium text-gray-900">{item.patientDetails?.patientName}</div>
                      <div className="text-xs text-gray-500">{item.patientDetails?.patientMobileNumber}</div>
                    </div>
                  </div>

                  {/* No.of Item Return */}
                  <div className="col-span-2 flex items-center">
                    <span className="text-sm font-medium text-gray-900">{item.returnItemsCount}</span>
                  </div>

                  {/* Total Price */}
                  <div className="col-span-2 flex items-center">
                    <span className="text-sm font-medium text-gray-900">₹{item.returnTotal}</span>
                  </div>

                  {/* Return Status */}
                  <div className="col-span-2 flex items-center">
                    <span className="text-xs text-gray-500">
                      {item.returnStatus === "returned_accepted"
                        ? "Approved"
                        : item.returnStatus === "returned_rejected"
                          ? "Rejected"
                          : "Pending"}
                    </span>
                  </div>

                  {/* Actions */}
                  <div className="col-span-1 flex items-center justify-center gap-2">
                    <button
                      onClick={() => handleReturnDetails(item, "accepted")}
                      className="px-4 py-1.5 text-xs font-medium text-teal-600 bg-teal-50 border border-teal-200 rounded-md hover:bg-teal-100 transition-colors"
                    >
                      View
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div className="col-span-12 py-8 text-center text-gray-500">
                {searchText ? `No results found for "${searchText}"` : "No data found"}
              </div>
            )}
            <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200">
              <div className="text-sm text-gray-500">
                Showing page {activeTab === "request" ? requestPagination.page : acceptedPagination.page} of{" "}
                {activeTab === "request" ? requestPagination.totalPages : acceptedPagination.totalPages}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">Rows per page:</span>
                <select
                  value={activeTab === "request" ? requestPagination.pageSize : acceptedPagination.pageSize}
                  onChange={(e) => {
                    const newSize = Number(e.target.value);
                    if (activeTab === "request") {
                      setRequestPagination((prev) => ({
                        ...prev,
                        pageSize: newSize,
                        page: 1,
                      }));
                    } else {
                      setAcceptedPagination((prev) => ({
                        ...prev,
                        pageSize: newSize,
                        page: 1,
                      }));
                    }
                  }}
                  className="text-sm border rounded-md px-2 py-1"
                >
                  <option value="5">5</option>
                  <option value="10">10</option>
                  <option value="20">20</option>
                  <option value="50">50</option>
                </select>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() =>
                    activeTab === "request"
                      ? handleRequestPageChange(requestPagination.page - 1)
                      : handleAcceptedPageChange(acceptedPagination.page - 1)
                  }
                  disabled={activeTab === "request" ? !requestPagination.hasPrevPage : !acceptedPagination.hasPrevPage}
                  className={`px-3 py-1 text-sm rounded-md ${(activeTab === "request" ? !requestPagination.hasPrevPage : !acceptedPagination.hasPrevPage)
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                    }`}
                >
                  Previous
                </button>
                <button
                  onClick={() =>
                    activeTab === "request"
                      ? handleRequestPageChange(requestPagination.page + 1)
                      : handleAcceptedPageChange(acceptedPagination.page + 1)
                  }
                  disabled={activeTab === "request" ? !requestPagination.hasNextPage : !acceptedPagination.hasNextPage}
                  className={`px-3 py-1 text-sm rounded-md ${(activeTab === "request" ? !requestPagination.hasNextPage : !acceptedPagination.hasNextPage)
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                    }`}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </>
      )}
      {showModal &&
        (modalType === "request" ? (
          <OrderReturnModal
            returnItemDetails={returnItemDetails}
            onClose={handleCloseModal}
            onAccept={() => handleAcceptReturn(returnItemDetails.returnId)}
            onReject={() => handleRejectReturn(returnItemDetails.returnId)}
          />
        ) : (
          <OrderAcceptModal
            returnAcceptedData={returnItemDetails}
            onClose={handleCloseModal}
            onUpdate={() => handleUpdateInventory(returnItemDetails.returnId)}
          />
        ))}
    </div>
  );
};

const OrderReturnModal = ({
  returnItemDetails,
  onClose,
  onAccept,
  onReject,
}: {
  returnItemDetails: any;
  onClose: () => void;
  onAccept: () => void;
  onReject: () => void;
}) => {

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-start p-6 border-b border-gray-200">
          <div className="flex gap-8">
            <div>
              <h3 className="text-sm font-medium text-gray-600 mb-1">Order Number</h3>
              <p className="text-lg font-semibold text-gray-900">{returnItemDetails.orderNumber}</p>
            </div>
            <div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full overflow-hidden">
                  <img
                    src={returnItemDetails.patientDetails.patientProfileImage || defaultAvatar}
                    alt={returnItemDetails.patientDetails.patientName}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <p className="font-semibold text-gray-900">{returnItemDetails.patientDetails.patientName}</p>
                  <p className="text-sm text-gray-500">{returnItemDetails.patientDetails.patientMobileNumber}</p>
                </div>
              </div>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="flex justify-between items-start mb-8">
            <div>
              <h3 className="text-sm font-medium text-gray-600 mb-1">Total Price</h3>
              <p className="text-2xl font-bold text-gray-900">₹{returnItemDetails?.returnTotal || 0}</p>
            </div>
            <div className="flex-1 max-w-md mx-8">
              <h3 className="text-sm font-medium text-gray-600 mb-2">Reason For Return</h3>
              <p className="text-gray-700">{returnItemDetails?.returnReason || "N/A"}</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={onAccept}
                className="px-6 py-2 bg-white border-2 border-green-500 text-green-600 font-medium rounded-lg hover:bg-green-50 transition-colors"
              >
                Accept
              </button>
              <button
                onClick={onReject}
                className="px-6 py-2 bg-white border-2 border-red-500 text-red-600 font-medium rounded-lg hover:bg-red-50 transition-colors"
              >
                Reject
              </button>
            </div>
          </div>

          {/* Product Details */}
          <div>
            <h3 className="text-lg font-semibold text-green-600 mb-4">PRODUCT DETAILS ({returnItemDetails?.returnItemsCount || 0} items)</h3>

            <div
              className="h-[140px] overflow-y-auto"
              style={{
                scrollbarWidth: "thin",
                scrollBehavior: "smooth",
              }}
            >
              <div className="bg-gray-50 rounded-lg overflow-hidden">
                {/* Table Header */}
                <div className="grid grid-cols-12 gap-4 p-4 bg-gray-100 border-b border-gray-200">
                  <div className="col-span-6 text-sm font-medium text-gray-700">Product Name</div>
                  <div className="col-span-3 text-sm font-medium text-gray-700">Quantity</div>
                  <div className="col-span-3 text-sm font-medium text-gray-700">Price</div>
                </div>

                {/* Product Rows */}
                {returnItemDetails?.productDetails.map((product: any, index: number) => (
                  <div key={index} className="grid grid-cols-12 gap-4 p-4 border-b border-gray-200 last:border-b-0">
                    <div className="col-span-6 flex items-center gap-3">
                      <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0">
                        <img src={product.productImage || defaultAvatar} alt={product.productName} className="w-full h-full object-cover" />
                      </div>
                      <div>
                        <span className="font-medium text-gray-900 block">{product.productName}</span>
                      </div>
                    </div>
                    <div className="col-span-3 flex items-center">
                      <span className="text-gray-700">{product.productQuantity} items</span>
                    </div>
                    <div className="col-span-3 flex items-center">
                      <span className="text-gray-700">₹{product.productPrice}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const OrderAcceptModal = ({
  returnAcceptedData,
  onClose,
  onUpdate,
}: {
  returnAcceptedData: any;
  onClose: () => void;
  onUpdate: () => void;
}) => {

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-start p-6 border-b border-gray-200">
          <div className="flex gap-8">
            <div>
              <h3 className="text-sm font-medium text-gray-600 mb-1">Order Number</h3>
              <p className="text-lg font-semibold text-gray-900">{returnAcceptedData.orderNumber}</p>
            </div>
            <div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full overflow-hidden">
                  <img
                    src={returnAcceptedData?.patientDetails.patientProfileImage || defaultAvatar}
                    alt={returnAcceptedData?.patientDetails.patientName}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <p className="font-semibold text-gray-900">{returnAcceptedData?.patientDetails.patientName}</p>
                  <p className="text-sm text-gray-500">{returnAcceptedData?.patientDetails.patientMobileNumber}</p>
                </div>
              </div>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="flex justify-between items-start mb-8">
            <div>
              <h3 className="text-sm font-medium text-gray-600 mb-1">Total Price</h3>
              <p className="text-2xl font-bold text-gray-900">₹{returnAcceptedData?.returnTotal}</p>
            </div>
            <div className="flex-1 max-w-md mx-8">
              <h3 className="text-sm font-medium text-gray-600 mb-2">Reason For Return</h3>
              <p className="text-gray-700">{returnAcceptedData?.returnReason}</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={onUpdate}
                className="px-6 py-2 bg-white border-2 border-green-500 text-green-600 font-medium rounded-lg hover:bg-green-50 transition-colors"
              >
                Update Inventory
              </button>
            </div>
          </div>

          {/* Product Details */}
          <div>
            <h3 className="text-lg font-semibold text-green-600 mb-4">PRODUCT DETAILS ({returnAcceptedData?.returnItemsCount || 0} items)</h3>

            <div
              className="h-[140px] overflow-y-auto"
              style={{
                scrollbarWidth: "thin",
                scrollBehavior: "smooth",
              }}
            >
              <div className="bg-gray-50 rounded-lg overflow-hidden">
                {/* Table Header */}
                <div className="grid grid-cols-12 gap-4 p-4 bg-gray-100 border-b border-gray-200">
                  <div className="col-span-6 text-sm font-medium text-gray-700">Product Name</div>
                  <div className="col-span-3 text-sm font-medium text-gray-700">Quantity</div>
                  <div className="col-span-3 text-sm font-medium text-gray-700">Price</div>
                </div>

                {/* Product Rows */}
                {returnAcceptedData?.productDetails?.map((product: any, index: number) => (
                  <div key={index} className="grid grid-cols-12 gap-4 p-4 border-b border-gray-200 last:border-b-0">
                    <div className="col-span-6 flex items-center gap-3">
                      <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0">
                        <img src={product?.productImage || defaultAvatar} alt={product?.productName} className="w-full h-full object-cover" />
                      </div>
                      <div>
                        <span className="font-medium text-gray-900 block">{product?.productName}</span>
                        <span className="text-xs text-gray-500">{product?.categoryName}</span>
                      </div>
                    </div>
                    <div className="col-span-3 flex items-center">
                      <span className="text-gray-700">{product?.productsItemsCount} items</span>
                    </div>
                    <div className="col-span-3 flex items-center">
                      <span className="text-gray-700">₹{product?.productPrice}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

interface ReturnHistoryInterface {
  id: number;
  productDetails: [
    {
      productName: string;
      productImage: string;
    }
  ];
  patientDetails: {
    patientName: string;
    patientProfileImage: string;
    patientMobileNumber: string;
  };
  returnItemsCount: number;
  returnTotal: number;
  returnReason: string;
  returnStatus: string;
}

const ReturnHistoryTable = ({ onClose }: { onClose: () => void }) => {
  const [itemHistory, setItemHistory] = useState<ReturnHistoryInterface[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    const fetchReturnOrders = async () => {
      try {
        const response = await ReturnItemService.getReturnItemHistoryListing();
        setItemHistory(response.data?.data || []);
        setTotalPages(response.data?.pagination?.totalPages || 1);
        setPageSize(response.data?.pagination?.pageSize || 10);
      } catch (error) {
        console.log(error);
      }
    };
    fetchReturnOrders();
  }, [currentPage, pageSize]);

  // Helper function to safely access history data with fallbacks
  const getHistoryData = (history: ReturnHistoryInterface) => {
    return (
      history || {
        id: "N/A",
        returnItemsCount: "N/A",
        returnTotal: "N/A",
        returnReason: "N/A",
        returnStatus: "N/A",
        patientDetails: {
          patientName: "N/A",
          patientProfileImage: null,
          patientMobileNumber: "N/A",
        },
        productDetails: [
          {
            productName: "N/A",
            productImage: defaultAvatar,
          },
        ],
      }
    );
  };

  const columns = [
    {
      key: "Order and Order Id",
      title: "Order and Order Id",
      sortable: true,
      width: "w-[250px]",
      render: (history: ReturnHistoryInterface) => {
        const data = getHistoryData(history);
        const product = data?.productDetails?.[0] || {
          productImage: defaultAvatar,
          productName: "N/A",
        };

        return (
          <div className="flex items-center">
            <img
              src={product.productImage || defaultAvatar}
              alt={product.productName}
              className="w-8 h-8 rounded-full mr-3 object-cover border border-gray-200"
            />
            <div>
              <div className="font-medium text-gray-900">{product.productName}</div>
              <div className="font-medium text-gray-900">{data.id || "N/A"}</div>
            </div>
          </div>
        );
      },
    },
    {
      key: "Patient Name and Number",
      title: "Patient Name and Number",
      sortable: true,
      width: "w-[250px]",
      render: (history: ReturnHistoryInterface) => {
        const data = getHistoryData(history);
        const patient = data?.patientDetails || {
          patientName: "N/A",
          patientProfileImage: null,
          patientMobileNumber: "N/A",
        };

        return (
          <div className="flex items-center">
            <img
              src={patient.patientProfileImage || defaultAvatar}
              alt={patient.patientName}
              className="w-8 h-8 rounded-full mr-3 object-cover border border-gray-200"
            />
            <div>
              <div className="font-medium text-gray-900">{patient.patientName}</div>
              <div className="font-medium text-gray-900">{patient.patientMobileNumber}</div>
            </div>
          </div>
        );
      },
    },
    {
      key: "Number Of Items Returned",
      title: "Number Of Items Returned",
      sortable: true,
      render: (history: ReturnHistoryInterface) => {
        const data = getHistoryData(history);
        return <div>{data.returnItemsCount || "N/A"}</div>;
      },
    },
    {
      key: "Total Price",
      title: "Total Price",
      sortable: true,
      render: (history: ReturnHistoryInterface) => {
        const data = getHistoryData(history);
        return <div>{data.returnTotal || "N/A"}</div>;
      },
    },
    {
      key: "Reason For Return",
      title: "Reason For Return",
      sortable: true,
      render: (history: ReturnHistoryInterface) => {
        const data = getHistoryData(history);
        return <div>{data.returnReason || "N/A"}</div>;
      },
    },
    {
      key: "Return Status",
      title: "Return Status",
      sortable: true,
      render: (history: ReturnHistoryInterface) => {
        const data = getHistoryData(history);
        return <div>{data.returnStatus || "N/A"}</div>;
      },
    },
  ];

  return (
    <>
      <button onClick={onClose} className="flex items-center justify-center">
        <CircleChevronLeft className="w-5 h-5 text-gray-500 hover:text-gray-700 mr-2" /> Back
      </button>
      <DataTable
        data={itemHistory}
        columns={columns}
        pagination={{
          currentPage,
          setCurrentPage,
          setTotalPages,
          totalPages,
          pageSize,
          setPageSize,
        }}
      />
    </>
  );
};

export const ClinicStockDashboardView: React.FC = () => {
  const [activeTab, setActiveTab] = useState("medical-orders");
  const [activeInventoryTab, setActiveInventoryTab] = useState("inventory-list");
  const [orders3, setOrders] = useState<OrderItem[]>([]);
  const [inventoryData, setInventoryData] = useState<unknown[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedView, setSelectedView] = useState<string | null>(null);
  const [showOverViews, setShowOverviews] = useState(false);

  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showReturnTable, setReturnTable] = useState(false);

  useEffect(() => {
    const fetchDashboardData = async () => {
      console.log("clinicId", user?.id);
      try {
        const clinicId = user?.role === "clinic" ? user?.id : "";
        const response = await OrderService.getDashboardOverview(clinicId);

        if (response.success === "invalid") {
          // Handle session expiration if needed
          setError("Session expired. Please log in again.");
          return;
        }

        if (response.success) {
          setDashboardData(response.data);
        } else {
          setError(response.message || "Failed to fetch dashboard data");
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to fetch dashboard data");
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const { user } = useAuth();

  const handleOrderTabChange = (tab: string) => {
    setActiveTab(tab);
  };

  const handleInventoryTabChange = (tab: string) => {
    setActiveInventoryTab(tab);
  };

  return (
    <>
      {showReturnTable ? (
        <ReturnHistoryTable onClose={() => setReturnTable(false)} />
      ) : (
        <>
          <div className="bg-gray-50 w-full">
            <>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <StatCard
                  title="Total Orders"
                  value={dashboardData?.totalOrders.count || 0}
                  icon={<img src={totalOrderIcon} alt="not-found" className="w-10 h-10" />}
                  changePercentage={dashboardData?.totalOrders.change || 0}
                  changeText="from last month"
                  onClick={() => {
                    setSelectedView("orders");
                    setShowOverviews(true);
                  }}
                />

                <StatCard
                  title="Total Orders Delivered"
                  value={dashboardData?.totalOrdersDelivered.count || 0}
                  icon={<img src={OrderDeliveryIcon} alt="not-found" className="w-10 h-10" />}
                  changePercentage={dashboardData?.totalOrdersDelivered.change || 0}
                  changeText="from last month"
                  onClick={() => {
                    setSelectedView("delivered");
                    setShowOverviews(true);
                  }}
                />

                <StatCard
                  title="Overall Lab Tests Conducted"
                  value={dashboardData?.labTestsConducted.count || 0}
                  icon={<img src={labTestIcon} alt="not-found" className="w-10 h-10" />}
                  changePercentage={dashboardData?.labTestsConducted.change || 0}
                  changeText="from last month"
                  onClick={() => {
                    setSelectedView("labTests");
                    setShowOverviews(true);
                  }}
                />

                <StatCard
                  title="Revenue YTD"
                  value={`₹ ${dashboardData?.revenue.amount.toLocaleString("en-IN") || 0}`}
                  icon={<img src={RevenueIcon} alt="not-found" className="w-10 h-10" />}
                  changePercentage={dashboardData?.revenue.change || 0}
                  changeText="increased from last month"
                />
              </div>

              <div className=" items-center justify-center gap-4 w-full  mx-auto space-y-6 mb-5">
                {/* OrderDetails Component */}
                <div className="relative">
                  {loading && (
                    <div className="absolute inset-0 bg-white bg-opacity-50 flex items-center justify-center z-10 rounded-lg">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500"></div>
                    </div>
                  )}
                  <OrderDetails activeTab={activeTab} orders={orders3} onTabChange={handleOrderTabChange} />
                </div>

                {/* InventoryDetails Component */}
                {/* <div className="relative">
                  {loading && (
                    <div className="absolute inset-0 bg-white bg-opacity-50 flex items-center justify-center z-10 rounded-lg">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500"></div>
                    </div>
                  )}
                  <InventoryDetails
                    activeInventoryTab={activeInventoryTab}
                    inventoryData={inventoryData}
                    onTabChange={handleInventoryTabChange}
                  />
                </div> */}
              </div>
              {user?.role.toLocaleLowerCase() === "clinic" && <ReturnManagement onShowReturnHistory={() => setReturnTable(true)} />}
            </>
          </div>
        </>
      )}
    </>
  );
};
